import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import Link, { LinkProps } from 'next/link';
import { Button, Flex } from '@chakra-ui/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import Vimeo from '@u-wave/react-vimeo';

import OpenGraph from '@/components/OpenGraph';
import Listings from '@/src/components/UI/Listings';
import FullPage from '@/src/components/UI/FullPage';
import useT from '@/lib/t';
import {
  CountryEnum,
  HomepageVimeoEntity,
  JobEntity,
  ListingDetail,
  StaticOptionEntity,
  UserStoryShowcaseEntity,
} from '@/swagger';
import { NextPage } from 'next';
import classNames from 'classnames';
import Image from 'next/image';
import { QA_DATA, QAItem } from '@/pages/abroad';
import { RiDoubleQuotesL } from 'react-icons/ri';
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi';
import ConditionalFragment from '@/components/common/ConditionalFragment';
import useIsTopOrEndReached from '@/src/hooks/useIsTopOrEndReached';
import Flexbox from '@/components/common/Flexbox';
import { isEmptyArray } from '@/utils/formatChecker';

import Banner1 from '@/public/assets/images/banner/home/home_banner-1.png';
import Banner2 from '@/public/assets/images/banner/home/home_banner-2.png';
import Banner3 from '@/public/assets/images/banner/home/home_banner-3.png';
import Banner4 from '@/public/assets/images/banner/home/home_banner-4.png';
import Banner5 from '@/public/assets/images/banner/home/home_banner-5.png';
import Banner6 from '@/public/assets/images/banner/home/home_banner-6.png';
import BannerMobile1 from '@/public/assets/images/banner/home/home_banner_mobile-1.png';
import BannerMobile2 from '@/public/assets/images/banner/home/home_banner_mobile-2.png';
import BannerMobile3 from '@/public/assets/images/banner/home/home_banner_mobile-3.png';
import BannerMobile4 from '@/public/assets/images/banner/home/home_banner_mobile-4.png';
import BannerMobile5 from '@/public/assets/images/banner/home/home_banner_mobile-5.png';
import BannerMobile6 from '@/public/assets/images/banner/home/home_banner_mobile-6.png';
import BannerPhoenix from '@/public/assets/images/banner/home/home_banner_phoenix.webp';
import BannerStudyAbroad from '@/public/assets/images/banner/home/home_banner_study_abroad.webp';
import BannerMobilePhoenix from '@/public/assets/images/banner/home/home_banner_mobile_phoenix.webp';
import BannerMobileStudyAbroad from '@/public/assets/images/banner/home/home_banner_mobile_study_abroad.webp';
import { BannerSetItem, HeroImageProps } from '@/src/components/UI/HeroImageProps';
import ReactIcon from '@/src/components/common/ReactIcon';
import ClientStorySection from '@/src/components/feature/ClientStorySection';
import clsx from 'clsx';
import { conferenceSerializer } from '@/src/helper/option';
import { ResolvedBannerHref } from '@/src/helper/bannerHref';

interface Props {
  listings: Array<ListingDetail>;
  jobs: Array<JobEntity>;
  conferenceOptions: Array<StaticOptionEntity>;
  userStories: Array<UserStoryShowcaseEntity>;
  banner: StaticOptionEntity;
  bannerHref: ResolvedBannerHref | null;
  vimeo: HomepageVimeoEntity;
}

const IMAGE_ASSETS = {
  abroad: 'https://cdn.funwoo.com.tw/assets/page/homepage/abroad.webp',
  userStoryG: 'https://cdn.funwoo.com.tw/assets/page/homepage/user_story_2024_miss_yen.jpg',
  userStoryF: 'https://cdn.funwoo.com.tw/assets/page/homepage/user_story_2023_irvine_mr_kong.jpg',
  userStoryA: 'https://cdn.funwoo.com.tw/assets/page/homepage/user_story_1_rect.webp',
  userStoryB: 'https://cdn.funwoo.com.tw/assets/page/homepage/user_story_2_rect.webp',
  userStoryC: 'https://cdn.funwoo.com.tw/assets/page/homepage/user_story_3_square.webp',
  userStoryD: 'https://cdn.funwoo.com.tw/assets/page/homepage/user_story_2020_bellevue.webp',
  userStoryE: 'https://cdn.funwoo.com.tw/assets/page/homepage/user_story_2023_irvine.webp',
  sellerServices: 'https://cdn.funwoo.com.tw/assets/page/homepage/seller_services_v2.webp',
};

const bannerSetItemHref = [
  'https://funwoo.com.tw/buy/703765',
  'https://funwoo.com.tw/building/963349',
  'https://funwoo.com.tw/building/248358',
  'https://funwoo.com.tw/building/818292',
  'https://funwoo.com.tw/building/101001',
  'https://funwoo.com.tw/building/443928',
];
const bannerSet = (
  process.env.IS_MOBILE === '1'
    ? [BannerMobile1, BannerMobile2, BannerMobile3, BannerMobile4, BannerMobile5, BannerMobile6]
    : [Banner1, Banner2, Banner3, Banner4, Banner5, Banner6]
)
  .map(
    ({ src }, index) =>
      ({
        src,
        href: bannerSetItemHref[index],
      } as NonNullable<HeroImageProps['bannerSets']>[number])
  )
  .filter((_, index) => [1, 2, 5].includes(index));

const isMobileBuild = process.env.IS_MOBILE === '1';

// Kept distinct from the site name below so Google doesn't treat the title as the site name.
const HOMEPAGE_TITLE = 'FUNWOO 美國不動產';

const HOMEPAGE_DESCRIPTION =
  'FUNWOO 德載國際不動產是臺灣領先的海外不動產置產顧問公司，專注於美國不動產市場的投資規劃與資產配置服務。';

// Declares the site name to Google so it stops treating <title> as the site name and
// rewriting the result's title link. Homepage only, per Google's site-name guidance.
const WEBSITE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'FUNWOO 德載國際不動產',
  alternateName: 'FUNWOO',
  url: 'https://www.funwoo.com.tw/',
};

// Seminar slides that follow the CMS banner in the homepage hero carousel. A function so the
// SeminarSlideContent reference (declared further down) is only read at render time.
const seminarSlides = (): BannerSetItem[] => [
  {
    src: (isMobileBuild ? BannerMobilePhoenix : BannerPhoenix).src,
    content: (
      <SeminarSlideContent
        headline={'複製竹北奇蹟 入駐鳳凰城'}
        description={
          '隨著全球半導體、AI 與先進製造業加速布局，美國鳳凰城持續吸引國際企業投資、人口移入與高薪就業機會，逐步成為全美最受矚目的新興城市之一。從產業發展、房市趨勢到海外置產策略，帶您掌握城市成長背後的關鍵機會。'
        }
        href={'/phoenix-seminar'}
      />
    ),
  },
  {
    src: (isMobileBuild ? BannerMobileStudyAbroad : BannerStudyAbroad).src,
    mask: true,
    content: (
      <SeminarSlideContent
        headline={'留學家庭 · 以房養學'}
        description={
          '美國匯聚全球頂尖教育資源，紐約與波士頓更是許多留學家庭長期關注的城市。從 Columbia、NYU 到 Harvard、MIT，不僅擁有世界級學府，也形成穩定的人口、租賃與住宅需求。對於計畫送孩子赴美留學的家庭而言，房子不只是孩子求學期間的居所，更可以成為家庭資產配置的一部分。透過提前規劃，在滿足居住需求的同時，也能將原本支付租金的支出，轉化為長期持有的房產資產。'
        }
        descriptionMaxWidth={1192}
        href={'/newyork-seminar'}
      />
    ),
  },
];

const Home: NextPage<Props> = ({ listings, conferenceOptions, userStories, banner, bannerHref }) => {
  const sellerServices = useRef([
    '專業菁英｜科技業、醫生等專業領域菁英',
    '企業主｜傳統產業、二代接班、家族傳承',
    '有教育需求者｜兒女留美，關注當地住宅',
  ]);

  const serializedConferenceOptions = useMemo(() => {
    return conferenceSerializer(conferenceOptions);
  }, [conferenceOptions]);

  return (
    <FullPage
      hasPadding={false}
      src='Home'
      hasBottomMargin={false}
      // banner={vimeo.vimeoId ? <VimeoBanner {...vimeo} /> : undefined}
      // bannerSrc={vimeo.vimeoId ? undefined : banner.value}
      bannerSets={[
        {
          src: banner.value,
          // Resolved in getStaticProps from a { type, sid } spec and re-checked
          // on each ISR revalidation. Null when the target is off-shelf, which
          // makes the banner render as a non-clickable element.
          href: bannerHref?.href,
          content: <PageAction />,
        },
        ...seminarSlides(),
      ]}
    >
      <OpenGraph title={HOMEPAGE_TITLE} imageUrl={banner.value} description={HOMEPAGE_DESCRIPTION} />
      <Head>
        <script
          key='website-json-ld'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(WEBSITE_JSON_LD) }}
        />
      </Head>
      <h1 className={'sr-only'}>FUNWOO 德載國際不動產｜美國不動產置產顧問</h1>
      {/*<FunwooHighlight />*/}
      <section className={'pt-4 pb-16 px-4'}>
        {isEmptyArray(serializedConferenceOptions) ? (
          <div className={'mb-8'}>
            <Heading>房屋精選</Heading>
            <p className={'text-center text-gray700'}>FUNWOO 將依照客戶購屋需求，快速精準推薦房源</p>
            <Flex
              py={{ base: 8 }}
              flexDirection={{ base: 'column', md: 'row' }}
              justifyContent={{ md: 'center' }}
              alignItems={{ sm: 'center' }}
            >
              <Link
                href={{
                  pathname: '/contact',
                  query: { country: CountryEnum.US },
                }}
                passHref
              >
                <Button as={'a'} w={{ base: 'full', sm: '425px', lg: '163px' }} fontWeight={500}>
                  立即填寫
                </Button>
              </Link>
            </Flex>
          </div>
        ) : (
          <div id={'conferences'} className={'-mt-15 sm:-mt-16'}>
            <div className={'pt-15 sm:pt-16'}>
              <Heading>美國置產說明會</Heading>
              <div
                className={classNames(
                  'p-6 space-y-4',
                  // Desktop: one shared 4-column grid so city/date/time/description line up across
                  // every session, with the whole table centered under the Heading.
                  'md:space-y-0 md:grid md:grid-cols-[repeat(4,auto)] md:justify-center md:items-center md:gap-x-20 md:gap-y-8'
                )}
              >
                {serializedConferenceOptions.map((option) => (
                  <ConferenceCard key={option.id} {...option} />
                ))}
              </div>
              <Flexbox align={'center'} justify={'center'} className={'mb-8'}>
                <Link href={'/abroad#contact-us'} passHref>
                  <Button as={'a'} fontWeight={500} id={'visit_lecture_registration_from_homepage'}>
                    說明會報名
                  </Button>
                </Link>
              </Flexbox>
            </div>
          </div>
        )}
        <Listings data={listings} />
        <Flex
          py={{ base: 8 }}
          flexDirection={{ base: 'column', md: 'row' }}
          justifyContent={{ md: 'center' }}
          alignItems={{ sm: 'center' }}
        >
          <Link
            href={{
              pathname: '/buy',
              query: { country: CountryEnum.US },
            }}
            passHref
          >
            <Button as={'a'} w={{ base: 'full', sm: '425px', lg: '163px' }} fontWeight={500}>
              查看更多房源
            </Button>
          </Link>
        </Flex>
      </section>
      <Abroad />
      <div id={'user-case'} className={'-mt-15 sm:-mt-16'}>
        <div className={'pt-15 sm:pt-16'}>
          <ClientStorySection title={'客戶故事'} subtitle={'打造賓至如歸的交易體驗'} stories={userStories} />
        </div>
      </div>
      <div className={'flex flex-col md:flex-row sm:items-center py-16 px-4 md:px-8'}>
        <div
          className={classNames(
            'relative mb-6 md:mb-0 md:flex-1 sm:w-full md:w-auto sm:max-w-[29.25rem] md:max-w-none'
          )}
        >
          <Image src={IMAGE_ASSETS.sellerServices} width={3} height={2} layout={'responsive'} />
        </div>
        <div className={'hidden md:block md:w-8'} />
        <div className={'md:flex-1'}>
          <div className={'md:px-8'}>
            <h2 className={classNames('mb-4', 'text-2xl leading-[3rem] text-gold font-medium')}>
              引領菁英佈局美國房市的領航者
            </h2>
            <p className={classNames('mb-4', 'leading-6 text-gray700')}>
              FUNWOO
              德載國際不動產是臺灣領先的海外不動產置產顧問公司，專注於美國不動產市場的投資規劃與資產配置服務。自成立以來，我們憑藉卓越的專業能力、豐富的實戰經驗與全面的客戶支持體系，穩居臺灣市場第一，成為眾多高資產淨值人士與機構信賴的合作夥伴。
            </p>
            <div className={classNames('mb-4')}>
              {sellerServices.current.map((service) => (
                <div key={service} className={'flex items-center text-lg leading-8 font-medium'}>
                  <div className={'flex items-center justify-center mr-2'}>
                    <span className={'block w-1 h-1 bg-gray900 rounded-[999px]'} />
                  </div>
                  <span>{service}</span>
                </div>
              ))}
            </div>
            <div className={'py-4'}>
              <Link href={'/about'} passHref>
                <Button
                  as={'a'}
                  display={{ base: 'flex' }}
                  width={{ base: 196 }}
                  mx={{ base: 'auto', md: '0' }}
                  fontWeight={500}
                  className={'items-center justify-center'}
                >
                  瞭解公司簡介
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/*<div className={'sm:flex sm:items-center sm:justify-center p-4 pb-[8rem] sm:pb-16 bg-gray50'}>*/}
      {/*  <div className={'sm:w-full sm:max-w-[29.25rem] md:max-w-[56.625rem]'}>*/}
      {/*    <Heading>加入 FUNWOO</Heading>*/}
      {/*    <p className={classNames('mb-8', 'leading-6 text-gray700 text-center')}>正在招募專業熱誠的你</p>*/}
      {/*    {jobs.map(({ id, sid, title }) => (*/}
      {/*      <Link key={id} href={`/jobs/${sid}`} passHref>*/}
      {/*        <a className={'border border-gray900 py-4 justify-between flex flex-row items-center px-3 w-full mb-4'}>*/}
      {/*          <p className=' leading-normal tracking-wide font-medium text-dark-grey uppercase'>{title}</p>*/}
      {/*          <Image*/}
      {/*            src={'/arrow-right.svg'}*/}
      {/*            alt={`click icon to read more about job ${title}`}*/}
      {/*            width={24}*/}
      {/*            height={24}*/}
      {/*          />*/}
      {/*        </a>*/}
      {/*      </Link>*/}
      {/*    ))}*/}
      {/*  </div>*/}
      {/*</div>*/}
    </FullPage>
  );
};
export default Home;

const VimeoBanner: React.FC<HomepageVimeoEntity> = ({ vimeoId, refTarget }) => {
  const Wrapper = useCallback<React.FC>(
    ({ children }) => {
      const refReg = /^(listing|building):(\d{6})$/;
      const [_, type, sid] = refTarget?.match(refReg) || [];
      if (type && sid) {
        return (
          <Link href={`/${type}/${sid}`} passHref>
            <a className={clsx('block', 'sm:py-6 sm:px-8')}>{children}</a>
          </Link>
        );
      } else {
        return <div className={clsx('sm:py-6 sm:px-8')}>{children}</div>;
      }
    },
    [refTarget]
  );

  return (
    <Wrapper>
      {/*@ts-ignore*/}
      <Vimeo
        video={vimeoId}
        autoplay
        controls={false}
        className={clsx('w-full', 'pointer-events-none')}
        width={'100%'}
        responsive
        muted
        loop
      />
    </Wrapper>
  );
};

const PageAction: React.FC = () => {
  const t = useT('pages.index');
  return (
    <Flex
      align={'center'}
      justify={'center'}
      position={'absolute'}
      className={classNames('top-1/2 left-1/2', 'transform -translate-x-1/2 -translate-y-1/2', 'pointer-events-auto')}
    >
      <Flex alignItems={'center'} flexDirection={'column'}>
        <p
          className={classNames(
            'text-[24px] leading-[42px] sm:text-[24px] sm:leading-[3rem] md:text-[24px]',
            'text-center font-medium uppercase',
            'text-white mb-4 text-shadow'
          )}
        >
          {t('slogan')}
        </p>
        <Flex flexDirection={{ base: 'column', md: 'row' }} className={''}>
          <PageActionButton href={'/abroad'}>服務說明</PageActionButton>
        </Flex>
      </Flex>
    </Flex>
  );
};
/**
 * Copy + CTA for a 說明會 slide in the hero carousel. Desktop (md+) lays it out left-aligned with the
 * description; narrower screens (and the mobile build) center it, break the title after
 * 《美國置產說明會》 and drop the description, per the Figma 首頁輪播 frames.
 */
const SeminarSlideContent: React.FC<{
  headline: string;
  description: string;
  /** Figma gives each slide its own description width; defaults to the 931px text column. */
  descriptionMaxWidth?: number;
  href: string;
}> = ({ headline, description, descriptionMaxWidth = 931, href }) => {
  return (
    <div
      className={classNames(
        'w-full h-full px-4 md:px-8 lg:pl-[160px]',
        'flex flex-col items-center justify-center text-center',
        'md:items-start md:justify-start md:text-left md:pt-[206px]',
        'text-white'
      )}
    >
      <p
        className={classNames(
          'font-medium [text-shadow:0_4px_80px_rgba(0,0,0,0.9)]',
          'text-[28px] leading-[36px] tracking-[0.15px] md:text-[40px] md:leading-[48px] lg:text-[48px] lg:leading-[42px] md:tracking-normal'
        )}
      >
        《美國置產說明會》
        <br className={'md:hidden'} />
        {headline}
      </p>
      <p
        className={'hidden md:block mt-6 text-[16px] leading-[24px] tracking-[0.5px]'}
        style={{ maxWidth: descriptionMaxWidth }}
      >
        {description}
      </p>
      <Link href={href} passHref>
        <Button
          as={'a'}
          border={0}
          variant='secondary'
          width={{ base: 202, md: 216 }}
          className={'mt-4 md:mt-6 pointer-events-auto'}
          fontWeight={500}
        >
          說明會報名
        </Button>
      </Link>
    </div>
  );
};
const PageActionButton: React.FC<{ href: LinkProps['href'] }> = ({ children, href }) => {
  return (
    <Link href={href} passHref>
      <Button
        as={'a'}
        border={0}
        variant='secondary'
        width={{ base: 192 }}
        className={'mb-4 last:mb-0 md:mb-0 md:mr-4 md:last:mr-0'}
        fontWeight={500}
      >
        {children}
      </Button>
    </Link>
  );
};
const Heading: React.FC<{ theme?: 'white' | 'black'; className?: string }> = ({
  children,
  theme = 'white',
  className,
}) => {
  return (
    <p
      className={classNames(className, 'py-2 text-2xl leading-[2rem] text-center font-medium', {
        'text-white': theme === 'black',
        'text-black': theme === 'white',
      })}
    >
      {children}
    </p>
  );
};

const ConferenceCard: React.FC<{
  city: string;
  date: string;
  time: string;
  description: string | null;
}> = ({ city, date, time, description }) => {
  // Mobile: a centered column per session. Desktop: `contents`, so the four <p>s become cells of the
  // parent's shared grid and each column aligns across sessions.
  return (
    <div id={date} className={'flex flex-col items-center gap-y-2 font-noto md:contents'}>
      <p className={'text-lg font-bold text-center md:text-left'}>{city}</p>
      <p className={'text-sm md:text-lg md:font-bold text-center md:text-left'}>{date}</p>
      <p className={'text-sm md:text-lg md:font-bold text-center md:text-left'}>{time}</p>
      <p className={'text-xs md:text-lg text-gray700 md:font-bold text-center md:text-left'}>{description}</p>
    </div>
  );
};

const Abroad: React.FC = () => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const [openCardIndex, setOpenCardIndex] = useState<number | null>(null);

  const dataContainerRef = useRef<HTMLDivElement | null>(null);

  const { scrollPositionChecker, onContainerScroll, topEndReached, bottomEndReached } = useIsTopOrEndReached({
    container: dataContainerRef.current,
  });

  const data = useMemo(() => (showMore ? QA_DATA : QA_DATA.slice(0, 3)), [showMore]);

  const triggerShowMore = useCallback(() => setShowMore((prev) => !prev), []);

  const onDataItemCollapse = useCallback(() => {
    scrollPositionChecker();
  }, []);

  const onCardClick = useCallback((cardIndex: number) => {
    setOpenCardIndex((prev) => (prev === cardIndex ? null : cardIndex));
  }, []);

  useEffect(() => {
    if (!dataContainerRef.current) return;
    onDataItemCollapse();
  }, [data]);

  return (
    <div
      className={classNames('flex flex-col md:flex-row py-16 px-4 md:p-16 md:px-8 md:max-h-[36rem] bg-gray50', {
        'sm:items-stretch': showMore,
        'sm:items-center': !showMore,
      })}
    >
      <div className={'md:flex-1 sm:w-full md:w-auto sm:max-w-[29.25rem] md:max-w-none md:flex-shrink-0'}>
        <div className={classNames('mb-6 md:mb-0 md:pr-4')}>
          <Image src={IMAGE_ASSETS.abroad} width={3} height={2} layout={'responsive'} />
        </div>
      </div>
      <div className={'md:flex-1 sm:max-w-[29.25rem] md:max-w-none md:flex-shrink-0'}>
        <div className={'md:flex md:flex-col md:pl-4 md:pr-8 md:max-h-[28rem] md:overflow-hidden'}>
          <h2 className={classNames('mb-4', 'text-2xl leading-[3rem] text-gray900 font-medium md:flex-shrink-0')}>
            美國置產服務
          </h2>
          <p className={classNames('mb-4', 'leading-6 text-gray700 md:flex-shrink-0')}>
            服務範圍涵蓋美國主要城市，由當地執業房產顧問為您解答所有海外置產問題，量身打造一條龍服務。
          </p>
          <div className={'relative mb-4 md:flex md:overflow-hidden md:flex-1'}>
            <div
              ref={dataContainerRef}
              onScroll={onContainerScroll}
              className={classNames('md:flex-1 md:overflow-scroll')}
            >
              {data.map(({ question, answer, subItems }, index) => (
                <QAItem
                  onCollapseEnd={onDataItemCollapse}
                  key={question}
                  question={question}
                  answer={answer}
                  subItems={subItems}
                  onCardClick={() => onCardClick(index)}
                  open={openCardIndex === index}
                />
              ))}
            </div>
            <div
              className={classNames('hidden absolute top-0 left-0 w-full h-12', { 'md:block': !topEndReached })}
              style={{
                backgroundImage: 'linear-gradient(to bottom, rgb(250, 250, 250) 0%, rgba(250, 250, 250, 0.27) 75%)',
              }}
            />
            <div
              className={classNames('hidden absolute bottom-0 left-0 w-full h-12', {
                'md:block': !bottomEndReached,
              })}
              style={{
                backgroundImage: 'linear-gradient(to top, rgb(250, 250, 250) 0%, rgba(250, 250, 250, 0.27) 75%)',
              }}
            />
          </div>
          <Button
            onClick={triggerShowMore}
            display={{ base: 'block' }}
            width={{ base: 196 }}
            mx={{ base: 'auto', md: '0' }}
            className={'md:flex-shrink-0'}
          >
            {showMore ? '隱藏' : '查看'}
            更多常見問答
          </Button>
        </div>
      </div>
    </div>
  );
};

export const UserStory: React.FC<{
  stories: Array<UserStoryShowcaseEntity>;
  shouldOffset?: boolean;
}> = ({ stories, shouldOffset = true }) => {
  const [swiper, setSwiper] = useState<SwiperCore>();
  const [activeIndex, setActiveIndex] = useState<number>(0);

  return (
    <div
      id={'user-case'}
      // className={'scroll-pt-15 sm:scroll-pt-16'}
      className={shouldOffset ? '-mt-15 sm:-mt-16' : ''}
    >
      <div className={shouldOffset ? 'pt-15 sm:pt-16' : ''}>
        <div className={'py-8 bg-black text-white'}>
          <Heading theme={'black'}>專業房產顧問團隊</Heading>
          <p className={'text-center'}>打造賓至如歸的交易體驗</p>
          <div className={'md:mb-2.5 py-6 md:py-16 px-4 sm:px-8 md:px-0'}>
            <div
              className={classNames('sm:relative', 'pt-16 md:pt-0', 'border border-solid border-white md:border-none')}
            >
              <Swiper
                onActiveIndexChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                className={'w-full md:px-8'}
                slidesPerView={1}
                spaceBetween={0}
                breakpoints={{ 1024: { slidesPerView: 1.5, spaceBetween: 64 } }}
                observer
                observeParents
                onSwiper={setSwiper}
              >
                {stories.map(({ id, buyer, content, photoUrl }) => (
                  <SwiperSlide
                    key={id}
                    className={classNames('px-4 sm:px-16 md:px-0', 'md:border md:border-solid md:border-white')}
                    style={{ height: 'auto' }}
                  >
                    <div className={'md:flex md:items-end md:p-16'}>
                      <div className={'flex items-center justify-center md:mr-8'}>
                        <div className={classNames('relative', 'w-[15.5rem] sm:w-[23.25rem] md:w-[15.5rem]')}>
                          <div className={'w-full pt-[100%] sm:pt-[calc(100%*2/3)] md:pt-[100%]'} />
                          <Image src={photoUrl} layout={'fill'} objectFit={'cover'} />
                        </div>
                      </div>
                      <div className={'md:relative'}>
                        <div
                          className={
                            'md:absolute md:top-0 md:transform md:-translate-y-full flex items-center justify-center md:justify-start'
                          }
                        >
                          <ReactIcon icon={RiDoubleQuotesL} size={80} />
                        </div>
                        <p className={'mb-4 text-xl leading-[1.6]'}>{content}</p>
                        <p className={'mb-8 md:mb-0 leading-6'}>{buyer}</p>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              <div
                className={'flex border-t sm:border-none border-solid border-white divide-x sm:divide-x-0 divide-white'}
              >
                <ConditionalFragment condition={activeIndex !== 0}>
                  <button
                    type={'button'}
                    className={classNames(
                      'sm:absolute sm:top-1/2 sm:-left-7 md:left-16',
                      'sm:transform sm:-translate-y-1/2',
                      'flex items-center justify-center py-4 flex-1',
                      'sm:w-14 sm:h-14',
                      'sm:bg-white sm:text-black'
                    )}
                    onClick={() => swiper?.slidePrev()}
                  >
                    <ReactIcon icon={HiOutlineChevronLeft} size={24} />
                  </button>
                </ConditionalFragment>
                <ConditionalFragment condition={activeIndex !== stories.length - 1}>
                  <button
                    type={'button'}
                    className={classNames(
                      'sm:absolute sm:top-1/2 sm:-right-7 md:right-16',
                      'sm:transform sm:-translate-y-1/2',
                      'flex items-center justify-center py-4 flex-1',
                      'sm:w-14 sm:h-14',
                      'sm:bg-white sm:text-black'
                    )}
                    onClick={() => swiper?.slideNext()}
                  >
                    <ReactIcon icon={HiOutlineChevronRight} size={24} />
                  </button>
                </ConditionalFragment>
              </div>
            </div>
          </div>
          {/*<Link href={'/agent'} passHref>*/}
          {/*  <a className={'block mx-auto py-2 px-8 w-fit leading-6 bg-white text-black'}>認識顧問團隊</a>*/}
          {/*</Link>*/}
        </div>
      </div>
    </div>
  );
};
